import { MutableRefObject, useCallback, useEffect, useRef, useState } from "react";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Graphic from "@arcgis/core/Graphic";
import _, { debounce } from "lodash";
import Sketch from "@arcgis/core/widgets/Sketch";
import * as equalsOperator from "@arcgis/core/geometry/operators/equalsOperator";
import { getSymbolForGeometryType } from "../../utils/arcgisHelper";

export enum HistoryOperationType {
  FeatureAdd = "feature_add",
  FeatureDelete = "feature_delete",
  FeatureGeometryUpdate = "feature_geometry_update",
  FeatureBatch = "feature_batch",
}

type FeatureAddHistoryRecord = {
  type: HistoryOperationType.FeatureAdd;
  graphicId: string;
  graphic: Graphic;
};

type FeatureDeleteHistoryRecord = {
  type: HistoryOperationType.FeatureDelete;
  graphicId: string;
  graphic: Graphic;
};

type FeatureGeometryUpdateHistoryRecord = {
  type: HistoryOperationType.FeatureGeometryUpdate;
  graphicId: string;
  prevGeometry: __esri.GeometryUnion;
  nextGeometry: __esri.GeometryUnion;
};

type FeatureBatchHistoryRecord = {
  type: HistoryOperationType.FeatureBatch;
  records: HistoryRecord[];
};

export type HistoryRecord =
  | FeatureAddHistoryRecord
  | FeatureDeleteHistoryRecord
  | FeatureGeometryUpdateHistoryRecord
  | FeatureBatchHistoryRecord;

interface UseFeatureSelectorHistoryOptions {
  editLayerRef: MutableRefObject<GraphicsLayer | null>;
  sketchRef: MutableRefObject<Sketch | null>;
  readdHandlers: () => any[];
  sketchCanUndo: boolean;
  sketchCanRedo: boolean;
}

export function useFeatureSelectorHistory({
  editLayerRef,
  sketchRef,
  readdHandlers,
  sketchCanUndo,
  sketchCanRedo,
}: UseFeatureSelectorHistoryOptions) {
  const [historyStack, setHistoryStack] = useState<HistoryRecord[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isUndoRedo, setIsUndoRedo] = useState(false);

  const canUndo = historyIndex >= 0 || sketchCanUndo;
  const canRedo = historyIndex < historyStack.length - 1 || sketchCanRedo;

  const prevFeaturesRef = useRef<Map<string, { attributes: Record<string, any>; geometry: __esri.GeometryUnion }>>(
    new Map(),
  );

  const originalFeaturesRef = useRef<Map<string, { attributes: Record<string, any>; geometry: __esri.GeometryUnion }>>(
    new Map(),
  );

  const isBatchingRef = useRef(false);
  const batchedOperationsRef = useRef<HistoryRecord[]>([]);
  const [changedFeatureIds, setChangedFeatureIds] = useState(new Set<string>());
  const pendingChangesRef = useRef(new Set<string>());

  const initialStateLoadedRef = useRef(false);

  const markFeatureAsChanged = (graphicId: string) => {
    const newSet = new Set(changedFeatureIds).add(graphicId);
    setIsUndoRedo(false);
    setChangedFeatureIds(newSet);
  };

  const processChangesDebounced = useCallback(
    debounce(() => {
      if (pendingChangesRef.current.size === 0) return;

      // Transfer pending changes to the active changes set
      const newSet = new Set([...Array.from(changedFeatureIds), ...Array.from(pendingChangesRef.current)]);
      setIsUndoRedo(false);
      setChangedFeatureIds(newSet);
      pendingChangesRef.current.clear();
    }, 500),
    [],
  );

  const markFeatureAsChangedDebounced = (graphicId: string) => {
    pendingChangesRef.current.add(graphicId);
    processChangesDebounced();
  };

  // Track feature changes
  useEffect(() => {
    if (!editLayerRef.current || isUndoRedo || changedFeatureIds.size === 0) return;

    const currentGraphics = editLayerRef.current.graphics.toArray();
    const currentGraphicsMap = new Map<string, { attributes: Record<string, any>; geometry: __esri.GeometryUnion }>();

    // Only process graphics that were marked as changed
    const changedGraphics = currentGraphics.filter((g) => changedFeatureIds.has(g.attributes?.graphicId));
    const changedGraphicsMap = _.keyBy(changedGraphics, (g) => g.attributes?.graphicId) as Record<string, Graphic>;

    changedGraphics.forEach((graphic) => {
      const graphicId = graphic.attributes?.graphicId;
      if (!graphicId) return;

      currentGraphicsMap.set(graphicId, {
        attributes: _.cloneDeep(graphic.attributes),
        geometry: graphic.geometry!.clone(),
      });
    });

    // Skip the first load
    if (!initialStateLoadedRef.current) {
      prevFeaturesRef.current = currentGraphicsMap;
      originalFeaturesRef.current = new Map(currentGraphicsMap);
      initialStateLoadedRef.current = true;
      return;
    }

    // Process changes
    const addedRecords: HistoryRecord[] = [];
    const updatedRecords: HistoryRecord[] = [];
    const deletedRecords: HistoryRecord[] = [];

    // Find added graphics
    Array.from(changedFeatureIds).forEach((graphicId) => {
      const graphic = changedGraphicsMap[graphicId];

      if (graphic && !prevFeaturesRef.current.has(graphicId)) {
        addedRecords.push({
          type: HistoryOperationType.FeatureAdd,
          graphicId,
          graphic: graphic.clone(),
        });
      }
    });

    // Find updated and deleted graphics
    Array.from(changedFeatureIds).forEach((graphicId) => {
      if (!prevFeaturesRef.current.has(graphicId)) return;

      const prevFeature = prevFeaturesRef.current.get(graphicId)!;
      const currentGraphic = changedGraphicsMap[graphicId];

      if (!currentGraphic) {
        // Feature was deleted
        const deletedGraphic = new Graphic({
          attributes: _.cloneDeep(prevFeature.attributes),
          geometry: prevFeature.geometry.clone(),
        });

        deletedRecords.push({
          type: HistoryOperationType.FeatureDelete,
          graphicId,
          graphic: deletedGraphic,
        });
        return;
      }

      const currentFeature = {
        attributes: currentGraphic.attributes,
        geometry: currentGraphic.geometry!,
      };

      // Check if geometry changed
      const didGeometryChange = !equalsOperator.execute(prevFeature.geometry, currentFeature.geometry);

      if (didGeometryChange) {
        updatedRecords.push({
          type: HistoryOperationType.FeatureGeometryUpdate,
          graphicId,
          prevGeometry: prevFeature.geometry.clone(),
          nextGeometry: currentFeature.geometry.clone(),
        });
      }
    });

    // Add all changes to history if there are any
    const allRecords = [...addedRecords, ...updatedRecords, ...deletedRecords];
    if (allRecords.length > 0) {
      if (isBatchingRef.current) {
        batchedOperationsRef.current.push(...allRecords);
      } else if (allRecords.length === 1) {
        addToHistory(allRecords[0]);
      } else {
        // If multiple records, batch them together
        addToHistory({
          type: HistoryOperationType.FeatureBatch,
          records: allRecords,
        });
      }
    }

    // Update ref for the next comparison - only for changed features
    Array.from(changedFeatureIds).forEach((graphicId) => {
      const graphic = currentGraphicsMap.get(graphicId);

      if (graphic?.geometry) {
        prevFeaturesRef.current.set(graphicId, {
          attributes: _.cloneDeep(graphic.attributes),
          geometry: graphic.geometry.clone(),
        });
      } else {
        // If graphic is no longer present, remove from tracking
        prevFeaturesRef.current.delete(graphicId);
      }
    });

    // Clear the changed features set after processing
    setChangedFeatureIds(new Set());
  }, [editLayerRef.current?.graphics.length, changedFeatureIds.size]);

  const addToHistory = (record: HistoryRecord) => {
    if (isUndoRedo) return;

    // If we're in the middle of the history stack, remove everything after current index
    setHistoryStack((prev) => {
      const undoStack = prev.slice(0, historyIndex + 1);
      return [...undoStack, record];
    });
    setHistoryIndex((prev) => prev + 1);
  };

  const startBatch = () => {
    isBatchingRef.current = true;
    batchedOperationsRef.current = [];
  };

  // End batching and record as a single history entry
  const endBatch = () => {
    if (batchedOperationsRef.current.length > 0) {
      addToHistory({
        type: HistoryOperationType.FeatureBatch,
        records: batchedOperationsRef.current,
      });
    }
    isBatchingRef.current = false;
    batchedOperationsRef.current = [];
  };

  // Cancel batching without recording
  const cancelBatch = () => {
    isBatchingRef.current = false;
    batchedOperationsRef.current = [];
  };

  const executeUndo = (record: HistoryRecord): boolean => {
    switch (record.type) {
      case HistoryOperationType.FeatureAdd:
        // Remove the added graphic
        if (!editLayerRef.current) return false;

        const graphicsToRemove = editLayerRef.current.graphics.filter(
          (g) => g.attributes?.graphicId === record.graphicId,
        );

        if (graphicsToRemove.length <= 0) return false;
        editLayerRef.current.graphics.removeMany(graphicsToRemove);
        return true;

      case HistoryOperationType.FeatureDelete:
        // Re-add the deleted graphic
        if (!editLayerRef.current) return false;
        if (record.graphic.geometry) record.graphic.symbol = getSymbolForGeometryType(record.graphic.geometry.type);
        editLayerRef.current.graphics.add(record.graphic);
        return true;

      case HistoryOperationType.FeatureGeometryUpdate:
        // Restore previous geometry
        if (!editLayerRef.current) return false;

        const graphicsToUpdateGeom = editLayerRef.current.graphics.filter(
          (g) => g.attributes?.graphicId === record.graphicId,
        );
        if (graphicsToUpdateGeom.length <= 0) return false;
        graphicsToUpdateGeom.getItemAt(0)!.geometry = record.prevGeometry;
        return true;

      case HistoryOperationType.FeatureBatch:
        // Execute each operation in the batch in reverse order
        let success = true;
        for (let i = record.records.length - 1; i >= 0; i--) {
          success = executeUndo(record.records[i]) && success;
        }
        return success;

      default:
        console.error("Unknown history record type", record);
        return false;
    }
  };

  const executeRedo = (record: HistoryRecord): boolean => {
    switch (record.type) {
      case HistoryOperationType.FeatureAdd:
        // Re-add the feature
        if (!editLayerRef.current) return false;
        if (record.graphic.geometry) record.graphic.symbol = getSymbolForGeometryType(record.graphic.geometry.type);
        editLayerRef.current.graphics.add(record.graphic);
        return true;

      case HistoryOperationType.FeatureDelete:
        // Delete the feature again
        if (!editLayerRef.current) return false;

        const graphicsToRemove = editLayerRef.current.graphics.filter(
          (g) => g.attributes?.graphicId === record.graphicId,
        );

        if (graphicsToRemove.length > 0) {
          editLayerRef.current.graphics.removeMany(graphicsToRemove);
          return true;
        }
        return false;

      case HistoryOperationType.FeatureGeometryUpdate:
        // Apply the next geometry
        if (!editLayerRef.current) return false;

        const graphicsToUpdateGeom = editLayerRef.current.graphics.filter(
          (g) => g.attributes?.graphicId === record.graphicId,
        );

        if (graphicsToUpdateGeom.length <= 0) return false;
        graphicsToUpdateGeom.getItemAt(0)!.geometry = record.nextGeometry;
        return true;

      case HistoryOperationType.FeatureBatch:
        // Execute each operation in the batch in original order
        let success = true;
        for (let i = 0; i < record.records.length; i++) {
          success = executeRedo(record.records[i]) && success;
        }
        return success;

      default:
        console.error("Unknown history record type", record);
        return false;
    }
  };

  const undo = () => {
    if (!canUndo) return false;
    const recordToUndo = historyStack[historyIndex];
    setIsUndoRedo(true);
    const success = executeUndo(recordToUndo);
    setHistoryIndex((prev) => prev - 1);
    readdHandlers();
    return success;
  };

  const redo = () => {
    if (!canRedo) return false;
    const recordToRedo = historyStack[historyIndex + 1];
    setIsUndoRedo(true);
    const success = executeRedo(recordToRedo);
    setHistoryIndex((prev) => prev + 1);
    readdHandlers();
    return success;
  };

  const handleUndo = () => {
    if (!canUndo) return;
    if (sketchCanUndo) {
      // If we're currently editing a feature, undo that
      sketchRef.current?.undo();
      return true;
    }
    sketchRef.current?.complete();
    undo();
  };

  const handleRedo = () => {
    if (!canRedo) return;
    if (sketchCanRedo) {
      // If we're currently editing a feature, redo that
      sketchRef.current?.redo();
      return true;
    }
    sketchRef.current?.complete();
    redo();
  };

  const clearHistory = () => {
    setHistoryStack([]);
    setHistoryIndex(-1);
  };

  return {
    undo,
    redo,
    handleUndo,
    handleRedo,
    canUndo,
    canRedo,
    startBatch,
    endBatch,
    cancelBatch,
    clearHistory,
    markFeatureAsChanged,
    markFeatureAsChangedDebounced,
  };
}
